import { ContactInformation } from './ContactInformation';
import { NewsSubscription } from './NewsSubscription';
import { PaymentAgreement } from './PaymentAgreement';
import { Payment } from './Payment';
import { PaymentMethod } from './PaymentMethod';
import { TermsOfAgreement } from './TermsOfAgreement';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { pipe } from 'fp-ts/lib/function';
import { SignupBody, useTypeSafeMutation } from '@oresundsbron/api';
import { FC, useCallback, useMemo, useState } from 'react';
import {
  fromPredicate,
  match,
  map,
  getOrElseW,
  sequenceArray,
  getOrElse,
  altW,
  of,
  fromNullable,
} from 'fp-ts/lib/Option';
import { isBackendError } from '../../../lib/errors';
import { useContracts } from '../../../hooks/useContracts';
import { constants, SignupResponse } from '@oresundsbron/validators';
import {
  businessPhoneCode,
  businessPrivatePhoneCode,
  isEntryEmpty,
} from '../utils';
import { tap } from '@oresundsbron/utilities';
import { Box, Spinner, Typography } from '@oresundsbron/bridge-ui';
import {
  setResponse,
  setRequestData,
  setError,
  setPayWithCreditCard,
  setToken,
} from '../stores/useSignUp';
import {
  ContractTranslationKey,
  getContractTranslationKey,
  TranslateableContracts,
} from '../../../lib/contract-translations';
import { SignUpProgress } from '../../../components/SignUpProgress';
import { getDefaultCurrency, toCurrency } from '../../../lib/currency';
import { toArray } from 'fp-ts/lib/ReadonlyArray';
import {
  filter,
  findFirst,
  flatten,
  map as mapA,
  reduce,
} from 'fp-ts/lib/Array';
import { useSubscribe } from '@oresundsbron/use-form';
import { Data } from '../../../components/Forms/TermsOfAgreement/Business';
import { Currency } from '@oresundsbron/type-safeguards';
import { isEmpty } from 'fp-ts/lib/Record';
import { strMatcher } from '~/lib/matchers';
import * as gtag from '~/lib/gtag';
import { MultiStepForm } from '~/components/Forms/MultiStepFrom';
import { BusinessCustomerData } from '~/components/Forms/ContactInformation/Business';
import { decodePhoneCode } from '~/lib/phone-code';
import { CodeVerification } from '../CodeVerification';
import { useBannerVisibility } from '@oresundsbron/app-common';

const creditCardOrInvoice =
  (
    onCard: (data: SignupBody) => void,
    onInvoice: (data: SignupBody) => Promise<void>
  ) =>
  (data: SignupBody) =>
    pipe(
      data,
      fromPredicate((b) => b.paymentMethod?.method === 'card'),
      match(
        () => onInvoice(data),
        () => Promise.resolve(onCard(data))
      )
    );

const onError = (e: unknown) =>
  pipe(
    e,
    fromPredicate(isBackendError),
    match(
      () => {},
      (e) => setError(e)
    )
  );

const SignUpForm: FC<{
  commuter?: boolean;
}> = ({ commuter }) => {
  const [loading, toggleLoading] = useState(false);
  const { locale } = useRouter();
  const { setBannerVisible } = useBannerVisibility();
  const { contracts, main } = useContracts(
    'business',
    commuter ? constants.OresundBusinessPendler : constants.OresundBusiness
  );

  const { push, asPath } = useRouter();

  const { mutateAsync } = useTypeSafeMutation(
    ['signup'],
    (req) => req.Customer.signup,
    {
      onMutate: () => {
        toggleLoading(true);
        setBannerVisible(false);
      },
      onSuccess: (data) => {
        setResponse(data as SignupResponse);
        setToken(data.code);
      },
      onError,
    }
  );

  const ns = commuter
    ? ['businessCommuterSignUp', 'common']
    : ['businessSignUp', 'common'];

  const { t } = useTranslation(ns, {
    nsMode: 'fallback',
  });

  const mapContactInfo = useCallback(
    (signupBody: SignupBody) =>
      pipe(
        signupBody,
        fromNullable,
        map(
          (body) =>
            ({
              ...body,
              contactInformation: pipe(
                body.contactInformation as BusinessCustomerData,
                businessPrivatePhoneCode.modify(decodePhoneCode),
                businessPhoneCode.modify(decodePhoneCode)
              ),
            } as SignupBody)
        ),
        getOrElse(() => signupBody)
      ),
    [businessPhoneCode, businessPrivatePhoneCode]
  );

  const submit = (data: SignupBody) => {
    const body = {
      ...data,
      contractType: main.value,
      payment: {
        paymentEntries: (data.payment.paymentEntries || []).filter(
          (entry) => !isEntryEmpty(entry)
        ),
      },
      paymentMethod: isEmpty(data.paymentMethod as Record<string, unknown>)
        ? undefined
        : data.paymentMethod,
    };
    return pipe(
      body,
      mapContactInfo,
      tap(setRequestData),
      fromPredicate((x) => x.paymentAgreement.oresundsPay === 'false'),
      map((d) => {
        mutateAsync(d);
      }),
      altW(() =>
        pipe(
          body,
          creditCardOrInvoice(
            () => setPayWithCreditCard(),
            async (data) => {
              const response = await mutateAsync(mapContactInfo(data));
              let info = {
                email: '',
                firstName: '',
                lastName: '',
                phone: '',
                phoneCode: '',
              };
              if ((body as any).contactInformation.contact) {
                info = (body as any).contactInformation.contact;
              } else {
                info = (body as any).contactInformation;
              }
              gtag.resetPurchase();
              gtag.purchase(
                {
                  transaction_id: response.orderNo,
                  value: pipe(
                    contracts,
                    mapA((contract) => contract.prices),
                    flatten,
                    filter(
                      (price) =>
                        price.currency === body.termsOfAgreement.currency
                    ),
                    reduce(0, (res, price) => res + price.price),
                    (totalPrice) => `${totalPrice}`
                  ),
                  currency: body.termsOfAgreement.currency,
                  coupon: body.termsOfAgreement.campaignCode,
                  items: contracts.map((contract) => ({
                    item_name: pipe(
                      contract.value as
                        | 'OresundGO'
                        | 'Oresund_Business'
                        | 'OresundPendler'
                        | 'Oresund_Business_Pendler'
                        | 'OresundPAY'
                        | 'OresundPAYBusiness',
                      strMatcher({
                        Oresund_Business_Pendler: () => 'OresundCommuter',
                        Oresund_Business: () => 'OresundBusiness',
                        OresundPAYBusiness: () => 'OresundPay',
                        OresundPendler: () => 'OresundCommuter',
                        OresundGO: () => 'OresundGo',
                        OresundPAY: () => 'OresundPay',
                      })
                    ),
                    item_category: 'Business',
                    item_variant: 'OperatorSignup',
                    quantity: '1',
                    price: pipe(
                      contract.prices,
                      findFirst(
                        (price) =>
                          price.currency === body.termsOfAgreement.currency
                      ),
                      map((price) => `${price.priceInclVat}`),
                      getOrElse(() => '')
                    ),
                  })),
                },
                {
                  email: info.email,
                  firstName: info.firstName,
                  lastName: info.lastName,
                  phone: `+${body.contactInformation.phoneCode}${body.contactInformation.phone}`,
                  city: body.contactInformation.city,
                  address1: body.contactInformation.address,
                  zipCode: body.contactInformation.zipCode,
                  company: (body as any).contactInformation.companyName ?? '',
                  country: body.contactInformation.country,
                }
              );
            }
          ),
          of
        )
      ),
      getOrElseW(() => Promise.resolve())
    );
  };

  const useAuth0 = process.env.NEXT_PUBLIC_USE_AUTH0 === 'true';

  const [currency] = useSubscribe<Data>('termsOfAgreement', 'currency');

  const displayCurrency = useMemo(
    () => (currency as Currency) || getDefaultCurrency(locale || ''),
    [currency, locale]
  );

  const c = useMemo(
    () =>
      pipe(
        contracts,
        mapA((c) =>
          pipe(
            c.value as TranslateableContracts,
            getContractTranslationKey,
            map((k) => ({
              value: t('price.yearly', {
                price: pipe(
                  c.prices || [],
                  findFirst((x) => x.currency === displayCurrency),
                  map((p) => toCurrency(p.price || 0, locale, displayCurrency)),
                  getOrElse(() => '')
                ),
              }),
              label: k,
              main: c.key === main.key,
            }))
          )
        ),
        sequenceArray,
        map(toArray),
        getOrElse(
          () =>
            [] as {
              label: ContractTranslationKey;
              value: string;
              main: boolean;
            }[]
        )
      ),
    [contracts, t, main, displayCurrency, locale]
  );

  if (loading) {
    return (
      <Box className="item-center col-span-full flex h-[600px] items-center justify-center text-5xl text-primary-600 md:col-span-6 md:col-start-4">
        <Spinner label={t('action.loading')} />
      </Box>
    );
  }

  return (
    <>
      <Typography intent="label" className="col-span-full mb-8">
        {t('form.requiredFields')}
      </Typography>

      <Box className="col-span-full md:col-span-8">
        <MultiStepForm
          onSubmit={submit}
          continueLabel={t('action.continue')}
          submitLabel={t('action.pay')}
          editLabel={t('action.edit')}
          push={(url) =>
            push(`${asPath.split('#')[0]}${url}`, undefined, {
              scroll: false,
              shallow: true,
            })
          }
        >
          <ContactInformation
            namespace="contactInformation"
            titleTranslationKey="form.sections.contactInformation.title"
            commuter={commuter}
            ns={ns}
          />
          {useAuth0 ? (
            <CodeVerification
              namespace="codeVerification"
              ns={ns}
              titleTranslationKey="form.sections.codeVerification.title"
              isBusiness={true}
            />
          ) : (
            <></>
          )}
          <PaymentAgreement
            namespace="paymentAgreement"
            titleTranslationKey="form.sections.paymentAgreement.title"
            contract={main.value}
            ns={ns}
          />
          <Payment
            namespace="payment"
            titleTranslationKey="form.sections.payment.title"
            commuter={commuter}
            ns={ns}
          />
          <PaymentMethod
            namespace="paymentMethod"
            titleTranslationKey="form.sections.paymentMethod.title"
            ns={ns}
          />
          <NewsSubscription
            namespace="subscribeToClub"
            titleTranslationKey="form.sections.newsSubscription.title"
            ns={ns}
          />
          <TermsOfAgreement
            namespace="termsOfAgreement"
            titleTranslationKey="form.sections.termsOfAgreement.title"
            contract={main.value}
            ns={ns}
          />
        </MultiStepForm>
      </Box>
      <Box className="hidden md:col-span-4 md:col-start-9 md:block">
        <SignUpProgress
          total={{
            label: t('signup.total'),
            value: t('price.yearly', {
              price: pipe(
                main.prices || [],
                findFirst((x) => x.currency === displayCurrency),
                map((p) => toCurrency(p.price || 0, locale, displayCurrency)),
                getOrElse(() => '')
              ),
            }),
          }}
          contracts={c}
          ns={commuter ? 'businessCommuterSignUp' : 'businessSignUp'}
          business={true}
        />
      </Box>
    </>
  );
};

export default SignUpForm;
